import {boundMethod} from "autobind-decorator";
import {sum} from "d3-array";
import React from "react";
import {injectIntl, IntlShape} from "react-intl";
import {Titled} from "react-titled";

import {EDocumentTypes} from "@/components/project/models";
import http from "@/services/http";
import {ERoles} from "@/services/models";
import session from "@/services/session";
import {EDeviceClasses} from "@toolbox/models";
import {IServerErrorCode} from "@toolbox/render-page/models";
import {ICentSopModel, ICycleDuration, ICycleModel, IDuration} from "../models";

import SopTasks from "@shared/sop/SopTasks";
import DocumentPairError from "@toolbox/building-blocks/DocumentPairError";
import TagsEditor from "@toolbox/building-blocks/TagsEditor";
import Card from "@toolbox/design/Card";
import Notes from "@toolbox/design/Notes";
import ContainerCol from "@toolbox/display-blocks/ContainerCol";
import {getDeviceDisplayName} from "@toolbox/functions/device-check";
import InlineRename from "@toolbox/nativ-inputs/InlineRename";
import Corrupted from "@toolbox/render-page/Corrupted";
import Loading from "@toolbox/render-page/Loading";
import MissingLicense from "@toolbox/render-page/MissingLicense";
import NotFound, {serverErrorCode} from "@toolbox/render-page/NotFound";
import T, {intl2Str} from "@translate/T";
import CentExecute from "./CentExecute";
import Channels from "./Channels";
import Cycles from "./Cycles";
import General from "./General";

export function getGlobalProfiles(value: ICycleModel[]) {
    return sum(value.map((x) => x.profiles));
}

export function getDurations(
    cycles: ICycleModel[],
    channelsCount: number,
    wavelengthsCount: number,
    deviceClass: EDeviceClasses,
): IDuration {
    let startTime = 0;
    const durations = [] as ICycleDuration[];

    let first = true;
    for (const cycle of cycles) {
        const {duration, min, limitedSchedule} = getDuration(
            cycle,
            channelsCount,
            wavelengthsCount,
            deviceClass,
            first,
        );
        const endTime = startTime + duration;

        durations.push({duration, endTime, startTime, min, limitedSchedule});

        startTime = endTime;
        first = false;
    }

    return {cycles: durations, total: startTime};
}

function getDuration(
    cycle: ICycleModel,
    channelsCount: number,
    wavelengthsCount: number,
    deviceClass: EDeviceClasses,
    first: boolean,
) {
    const interval = Math.max(cycle.interval, 0.3); // devices takes minimum of 0.3 seconds to record on profile
    const processTime = channelsCount * wavelengthsCount * 0.15; // if takes 0.15 seconds to prozess a image (per channel and per wavelength)
    let profiles = cycle.profiles;
    let min = 1;

    // remove once the time of the first cycle, since photo will be taken instantly
    if (deviceClass === EDeviceClasses.XRay && first) {
        profiles -= 1;
        min = 2;
    }

    let limitedSchedule = false;
    let duration = profiles * interval;
    if (processTime > interval) {
        limitedSchedule = true;
        duration = profiles * processTime;
    }

    return {duration, min, limitedSchedule};
}

interface ISopInfoProps {
    intl: IntlShape;
    sop: number;
    project: number;

    readonly?: boolean;
}

interface ISopInfoState extends IServerErrorCode {
    name: string;
    tags: string[];

    sop?: ICentSopModel;
    duration?: IDuration;
}

class SopInfo extends React.PureComponent<ISopInfoProps, ISopInfoState> {
    public readonly state: ISopInfoState = {name: "", tags: []};

    @boundMethod
    public async rename(name: string) {
        try {
            const response = await http.post(
                `/api/centrifuge/sop/${this.props.sop}/rename`,
                {json: {name}},
            );
            if (!response.ok) {
                throw new http.HTTPError(response);
            }

            this.setState({name});

            return true;
        } catch {
            return false;
        }
    }

    @boundMethod
    public updateTags(tags: string[]) {
        this.setState({tags});
    }

    public async componentDidMount() {
        await this.retrieve();
    }

    public async componentDidUpdate(nextProps: ISopInfoProps) {
        if (nextProps.sop !== this.props.sop) {
            // navigate to another sop, needed for sop-queue
            await this.retrieve();
        }
    }

    @boundMethod
    public getTitle(parent: string) {
        const {intl} = this.props;
        const {sop} = this.state;
        const name = getDeviceDisplayName(
            sop?.device?.deviceClass ?? EDeviceClasses.Reader,
        );

        return intl2Str(intl, "{name} SOP", {name}) + " | " + parent;
    }

    public render() {
        const props = this.props;
        const {
            cannotBePaired,
            corrupted,
            duration,
            missingSopLicenses,
            name,
            notFound,
            sop,
            tags,
        } = this.state;

        if (corrupted) {
            return <Corrupted />;
        }

        if (notFound) {
            return <NotFound id={props.sop} item={<T>The specified SOP</T>} />;
        }

        if (cannotBePaired) {
            return <DocumentPairError />;
        }

        if (missingSopLicenses) {
            return <MissingLicense missingSopLicenses={missingSopLicenses} />;
        }

        if (!sop) {
            return <Loading />;
        }

        return (
            <Titled title={this.getTitle}>
                <ContainerCol>
                    {this.renderSopTasks(sop)}

                    {this.renderTitle()}

                    <InlineRename
                        id="centrifuge-sop-info-name"
                        classNameDiv="mb-2"
                        h5Title={true}
                        nameCheckApi={`/api/projects/${props.project}/namecheck`}
                        nameCheckId={sop.id}
                        readonly={
                            props.readonly ||
                            (!session.hasRole(ERoles.Manager, sop.project) &&
                                !session.hasRole(ERoles.Editor, sop.project))
                        }
                        type={EDocumentTypes.Sop}
                        value={name}
                        onChanged={this.rename}
                    />

                    <div className="form-row">
                        <div className="col-lg-6">
                            <General sop={sop} duration={duration!.total} />
                        </div>

                        <div className="col-lg-6">
                            <Card
                                headerClassName="bg-secondary"
                                title={<T>Comments</T>}
                            >
                                <div id="notes" className="card-body">
                                    <Notes value={sop.notes} suffixId="info" />
                                </div>
                            </Card>
                        </div>
                    </div>

                    <Channels channels={sop.channels} />

                    <Cycles
                        device={sop.device}
                        cycles={sop.cycles}
                        durations={duration!.cycles}
                    />

                    <TagsEditor
                        value={tags}
                        project={sop.project}
                        endpoint={`/api/tags/centrifuge/sop/${sop.id}`}
                        onChanged={this.updateTags}
                        readonly={props.readonly}
                    />
                </ContainerCol>
            </Titled>
        );
    }

    private renderSopTasks(sop: ICentSopModel) {
        if (this.props.readonly) {
            return null;
        }

        return (
            <SopTasks
                project={sop.project}
                id={sop.id}
                name={sop.name}
                owner={sop.owner}
                deviceClass={sop.device.deviceClass}
                renderExecute={this.renderExecute}
            />
        );
    }

    @boundMethod
    private renderTitle() {
        const {intl} = this.props;
        const {sop} = this.state;
        const name = getDeviceDisplayName(
            sop?.device?.deviceClass ?? EDeviceClasses.Reader,
        );

        return (
            <React.Fragment>
                <h3 id="sop-info-page">
                    {intl2Str(intl, "{name} SOP", {name})}
                </h3>
                <hr />
            </React.Fragment>
        );
    }

    @boundMethod
    private renderExecute(messages: React.RefObject<HTMLDivElement>) {
        const sop = this.state.sop!;

        return <CentExecute sop={sop} messages={messages} />;
    }

    private async retrieve() {
        const {readonly, sop, project} = this.props;
        const api = readonly ? "queue/" : "";

        try {
            const response = await http
                .get(`/api/centrifuge/sop/${api}${project}/${sop}`)
                .json<ICentSopModel>();

            const duration = getDurations(
                response.cycles,
                response.channels.length,
                response.wavelengths.length,
                response.device.deviceClass,
            );
            this.setState({
                sop: response,

                duration,
                name: response.name,
                tags: response.tags,

                // necessary to open queued sops after a fail
                cannotBePaired: undefined,
                corrupted: undefined,
                missingSopLicenses: undefined,
                notFound: undefined,
            });
        } catch (error) {
            if (error instanceof http.HTTPError) {
                this.setState(await serverErrorCode(error.response));
            }

            this.setState({
                name: "",
                tags: [],
                sop: undefined,
                duration: undefined,
            });
        }
    }
}

export default injectIntl(SopInfo);
